import React, { useEffect, useState } from 'react'
import { Modal, Tooltip } from 'antd'
import { FaAllergies, FaHeartbeat, FaRegCalendarAlt } from 'react-icons/fa'
import { MdOutlineHealthAndSafety } from 'react-icons/md'
import { GiMedicines } from 'react-icons/gi'
import { FiPlus, FiX } from 'react-icons/fi'
import { RiShieldCrossLine } from 'react-icons/ri'
import AddAllergyDetails from '../ConsultationNotes/VitalTabs/MedicalHistory/AddAllergyDetails'
import AddChronicCondition from '../ConsultationNotes/VitalTabs/MedicalHistory/AddChronicCondition'
import MedicalHistoryService from '../../../Services/MedicalHistoryService'
import { useSelector } from 'react-redux'
import dayjs from 'dayjs'

const ChCoAndAllDetails = ({ appointmentDetails }) => {
    const [showAllergy, setShowAllergy] = useState(false)
    const [showChronic, setShowChronic] = useState(false)
    const [addAllergyOpen, setAddAllergyOpen] = useState(false)
    const [addChronicOpen, setAddChronicOpen] = useState(false)
    const [allergyData, setAllergyData] = useState([])
    const [chronicData, setChronicData] = useState([])
    const { selectedRpmPatient } = useSelector((state) => state.rpm)

    const getPatientCode = () => {
        const patientRaw = sessionStorage.getItem('patient')
        const patientData = patientRaw ? JSON.parse(patientRaw) : null
        return patientData?.patientId || selectedRpmPatient?.patientId
    }

    const fetchAllergies = () => {
        const patientCode = getPatientCode()
        const payload = { userId: '', patientCode, status: 1 }
        MedicalHistoryService.PatientAllergiesList(payload)
            .then((res) => setAllergyData(res.data || []))
            .catch(console.error)
    }

    const fetchChronic = () => {
        const patientId = getPatientCode()
        const payload = { patientId, userId: '', status: 1 }
        MedicalHistoryService.PatientchronicConditions(payload)
            .then((res) => setChronicData(res.data || []))
            .catch(console.error)
    }

    useEffect(() => { if (showAllergy) fetchAllergies() }, [showAllergy])
    useEffect(() => { if (showChronic) fetchChronic() }, [showChronic])

    const getAllergiesList = (user) => {
        if (!user) return []
        const list = []
        if (user.drugAllergy?.trim()) list.push({ label: 'Drug Allergies', value: user.drugAllergy })
        if (user.foodAllergy?.trim()) list.push({ label: 'Food Allergies', value: user.foodAllergy })
        if (user.chemicalAllergy?.trim()) list.push({ label: 'Chemical Allergies', value: user.chemicalAllergy })
        if (user.otherAllergy?.trim()) list.push({ label: 'Other Allergies', value: user.otherAllergy })
        return list
    }

    const validAllergyRecords = (allergyData || []).filter(u => getAllergiesList(u).length > 0)

    return (
        <>
            {/* Trigger Buttons */}
            <button
                title="Allergy Details"
                onClick={() => setShowAllergy(true)}
                className="p-2 text-sm bg-white border text-white rounded-md hover:bg-blue-600 transition w-8 h-8 flex items-center justify-center"
            >
                <FaAllergies size={15} className="text-blue-500" />
            </button>

            <button
                title="Chronic Conditions"
                onClick={() => setShowChronic(true)}
                className="p-2 text-sm bg-white border text-white rounded-md hover:bg-green-600 transition ml-2 w-8 h-8 flex items-center justify-center"
            >
                <FaHeartbeat size={15} className="text-green-500" />
            </button>

            {/* CHRONIC CONDITIONS POPUP */}
            {showChronic && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setShowChronic(false)} />
                    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
                        <div className="flex items-start justify-between px-6 pt-6 pb-4">
                            <div className="flex items-center gap-3">
                                <div className="w-11 h-11 rounded-xl bg-[#E8F8F5] flex items-center justify-center text-[#0aa097] text-2xl flex-shrink-0">
                                    <MdOutlineHealthAndSafety />
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold text-[#143250]">Chronic Conditions</h2>
                                    <p className="text-xs text-gray-400 mt-0.5">Manage patient chronic conditions history</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setAddChronicOpen(true)}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0aa097] text-white text-sm font-semibold rounded-lg hover:bg-teal-600 transition-colors shadow-sm"
                                >
                                    <FiPlus className="text-sm" /> Add
                                </button>
                                <button
                                    onClick={() => setShowChronic(false)}
                                    className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 transition-colors"
                                >
                                    <FiX className="text-base" />
                                </button>
                            </div>
                        </div>

                        <div className="px-6 pb-6 max-h-80 overflow-y-auto flex flex-col gap-3 custom-scrollbar">
                            {chronicData.length > 0 ? chronicData.map((item, i) => (
                                <div key={i} className="bg-[#F4FDFD] border border-[#c5f0ec] border-l-4 border-l-[#0aa097] rounded-xl p-4 flex items-center gap-4">
                                    <div className="w-10 h-10 rounded-full bg-[#E8F8F5] text-[#0aa097] flex items-center justify-center text-lg flex-shrink-0">
                                        <MdOutlineHealthAndSafety />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-[11px] text-gray-400 font-medium mb-0.5">Chronic Condition</p>
                                        <p className="text-[14px] font-bold text-[#143250] truncate">{item.chronicDescription}</p>
                                    </div>
                                    {item.givenDate && (
                                        <div className="flex items-center gap-2 bg-white rounded-xl px-3 py-2 border border-gray-100 shadow-sm flex-shrink-0">
                                            <FaRegCalendarAlt className="text-[#0aa097] text-base" />
                                            <div>
                                                <p className="text-[11px] text-gray-400 font-medium">Onset Date</p>
                                                <p className="text-[13px] font-bold text-[#143250]">{dayjs(item.givenDate).format('YYYY-MM-DD')}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )) : (
                                <div className="text-center py-10 text-gray-400 text-sm bg-gray-50 rounded-xl border border-dashed border-gray-200">
                                    No chronic conditions found.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ALLERGY DETAILS POPUP */}
            {showAllergy && (
                <div className="fixed inset-0 z-50 flex items-center justify-center">
                    <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setShowAllergy(false)} />
                    <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
                        <div className="flex items-start justify-between px-6 pt-6 pb-4">
                            <div className="flex items-center gap-3">
                                <div className="w-11 h-11 rounded-xl bg-[#E8F8F5] flex items-center justify-center text-[#0aa097] text-2xl flex-shrink-0">
                                    <RiShieldCrossLine />
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold text-[#143250]">Allergy Details</h2>
                                    <p className="text-xs text-gray-400 mt-0.5">Manage patient allergy history</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setAddAllergyOpen(true)}
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-[#0aa097] text-white text-sm font-semibold rounded-lg hover:bg-teal-600 transition-colors shadow-sm"
                                >
                                    <FiPlus className="text-sm" /> Add
                                </button>
                                <button
                                    onClick={() => setShowAllergy(false)}
                                    className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-500 hover:bg-gray-200 transition-colors"
                                >
                                    <FiX className="text-base" />
                                </button>
                            </div>
                        </div>

                        <div className="px-6 pb-6 max-h-80 overflow-y-auto flex flex-col gap-3 custom-scrollbar">
                            {validAllergyRecords.length > 0 ? validAllergyRecords.map((user, i) => {
                                const allergies = getAllergiesList(user)
                                const first = allergies[0]
                                
                                const tooltipContent = (
                                    <div className="flex flex-col gap-1 p-1">
                                        {allergies.map((item, idx) => (
                                            <p key={idx} className="text-xs"><strong>{item.label}:</strong> {item.value}</p>
                                        ))}
                                    </div>
                                )
                                
                                return (
                                    <Tooltip key={i} title={tooltipContent} placement="top" color="#143250">
                                        <div className="bg-[#F4FDFD] border border-[#c5f0ec] border-l-4 border-l-[#0aa097] rounded-xl p-4 flex items-center gap-4 cursor-pointer hover:bg-[#E8F8F5] transition-colors">
                                            <div className="w-10 h-10 rounded-full bg-[#E8F8F5] text-[#0aa097] flex items-center justify-center text-lg flex-shrink-0">
                                                <GiMedicines />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-[11px] text-gray-400 font-medium mb-0.5">{first.label}</p>
                                                <p className="text-[14px] font-bold text-[#143250] truncate">{first.value}</p>
                                            </div>
                                            {user.givenDate && (
                                                <div className="flex items-center gap-2 bg-white rounded-xl px-3 py-2 border border-gray-100 shadow-sm flex-shrink-0">
                                                    <FaRegCalendarAlt className="text-[#0aa097] text-base" />
                                                    <div>
                                                        <p className="text-[11px] text-gray-400 font-medium">Onset Date</p>
                                                        <p className="text-[13px] font-bold text-[#143250]">{dayjs(user.givenDate).format('YYYY-MM-DD')}</p>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </Tooltip>
                                )
                            }) : (
                                <div className="text-center py-10 text-gray-400 text-sm bg-gray-50 rounded-xl border border-dashed border-gray-200">
                                    No allergy records found.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Add Allergy Modal */}
            <Modal
                title={<span className="font-bold text-base">Add Allergy Details</span>}
                open={addAllergyOpen}
                onCancel={() => setAddAllergyOpen(false)}
                footer={null}
            >
                <AddAllergyDetails
                    handleOk={() => setAddAllergyOpen(false)}
                    handleCancel={() => setAddAllergyOpen(false)}
                    patientAllergies={fetchAllergies}
                    onClose={() => setAddAllergyOpen(false)}
                    handleLatestUpdate={setAllergyData}
                    permissionData={'Y'}
                />
            </Modal>

            <Modal
                title={<span className="font-bold text-base">Add Chronic Condition</span>}
                open={addChronicOpen}
                onCancel={() => setAddChronicOpen(false)}
                footer={null}
            >
                <AddChronicCondition
                    onClose={() => setAddChronicOpen(false)}
                    handleLatestUpdate={setChronicData}
                    permissionData={'Y'}
                />
            </Modal>
        </>
    )
}

export default ChCoAndAllDetails
